import { useCallback, useEffect, useState } from "react";
import { CalendarClock, Loader2, Send, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { JaritBadge } from "@/components/JaritBadge";
import { EmployeeName } from "@/components/EmployeeName";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const todayMks = () => new Date(Date.now() + 8 * 3600 * 1000).toISOString().slice(0, 10);
const RESULT_STYLE = {
  FULL_DAY: "bg-[#E9F9EF] text-[#0E8A46]", HALF_DAY: "bg-[#E7F0FF] text-[#0058C7]",
  ZERO: "bg-[#FDECEC] text-[#C0362C]", NEEDS_REVIEW: "bg-[#FFF1E6] text-[#B5590F]",
  WORKING: "bg-[#E6F7F5] text-[#0E7C86]", IGNORED: "bg-[#F1F1F4] text-[#6E6E73]",
};
const REASON = {
  MISSING_PAIR: "Missing Clock Out", LATE_ARRIVAL: "Late Arrival", EARLY_DEPARTURE: "Early Departure",
  AMBIGUOUS_HALF_DAY: "Ambiguous Half Day", UNUSUAL_SCAN_PATTERN: "Unusual Scan Pattern",
  NEW_SCAN_AFTER_REVIEW: "New Scan After Review", MANUAL_ENTRY: "Manual Entry",
};
const nice = (s) => (s || "—").replace(/_/g, " ");
const STATUS_CHIP = { pending: "bg-[#FFF1E6] text-[#B5590F]", approved: "bg-[#E9F9EF] text-[#0E8A46]", rejected: "bg-[#FDECEC] text-[#C0362C]" };
const STATUS_ID = { pending: "Menunggu Persetujuan", approved: "Disetujui", rejected: "Ditolak" };

export default function AttendanceCorrection() {
  const [date, setDate] = useState(todayMks());
  const { readOnly } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [propose, setPropose] = useState(null); // { row, action, day_value, reason }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, h] = await Promise.all([
        api.get("/attendance/fingerprint/daily-view", { params: { date } }),
        api.get("/attendance/requests"),
      ]);
      setRows(d.data?.rows || []);
      setHistory(h.data || []);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!propose?.reason?.trim()) { toast.error("Alasan wajib diisi"); return; }
    setBusy(true);
    try {
      const r = await api.post("/attendance/requests", {
        employee_id: propose.row.employee_id, date,
        action: propose.action, day_value: propose.action === "ignore" ? 0 : propose.day_value,
        reason: propose.reason.trim(),
      });
      toast.success(r.data?.auto_approved
        ? "Koreksi tukang jarit otomatis disetujui"
        : "Permintaan koreksi dikirim ke admin");
      setPropose(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="attendance-correction-page" className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-[22px] font-semibold tracking-tight sm:text-[28px]">
            <CalendarClock className="h-6 w-6 text-[#0071E3]" /> Koreksi Absensi
          </h1>
          <p className="mt-0.5 text-[13px] text-[#8E8E93]">Lihat absensi dan ajukan koreksi. Hasil absensi tidak berubah sampai admin menyetujui.</p>
        </div>
        <input data-testid="ac-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10 rounded-full border border-[#E1E1E6] bg-white/70 px-3.5 text-[13px]" />
      </div>

      {loading ? (
        <div className="grid place-items-center py-14"><Loader2 className="h-5 w-5 animate-spin text-[#B0B0B8]" /></div>
      ) : rows.length === 0 ? (
        <div data-testid="ac-empty" className="liquid-glass-card grid place-items-center py-14 text-center text-[13.5px] text-[#8E8E93]">Belum ada data absensi untuk tanggal ini.</div>
      ) : (
        <div className="liquid-glass-card overflow-hidden">
          <div className="space-y-2.5 p-3 sm:p-4">
            {rows.map((r) => (
              <div key={r.id} data-testid={`ac-row-${r.employee_id}`} className="flex flex-col gap-3 rounded-2xl border border-black/[0.05] bg-white/70 p-3.5 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-3">
                  <EmployeeAvatar id={r.employee_id} nama={r.employee_name} hasPhoto={r.has_photo} size={38} />
                  <div className="min-w-0">
                    <p className="flex items-center gap-1.5 text-[14px] font-semibold text-[#1D1D1F]"><EmployeeName id={r.employee_id} name={r.employee_name} /><JaritBadge show={r.is_jarit} /></p>
                    <p className="num text-[11.5px] text-[#8E8E93]">{r.employee_nip || "—"} · {r.clock_in || "—"} → {r.clock_out || "—"}</p>
                    {r.status === "NEEDS_REVIEW" && r.reason_code && <p className="mt-0.5 text-[11.5px] font-medium text-[#B5590F]">{REASON[r.reason_code] || nice(r.reason_code)}</p>}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className={`rounded-full px-2.5 py-1 text-[11.5px] font-medium ${RESULT_STYLE[r.result] || "bg-[#F1F1F4] text-[#6E6E73]"}`}>{nice(r.result)}</span>
                  <button data-testid={`ac-propose-${r.employee_id}`} onClick={() => setPropose({ row: r, action: "approve", day_value: 0.5, reason: "" })} className="flex h-8 items-center gap-1.5 rounded-full border border-[#E1E1E6] bg-white px-3 text-[12.5px] font-medium text-[#0071E3] transition-all active:scale-[0.97]"><Send className="h-3.5 w-3.5" /> Ajukan Koreksi</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h2 className="mb-2 flex items-center gap-2 text-[14px] font-semibold tracking-tight"><Clock className="h-4 w-4 text-[#0071E3]" /> Permintaan Saya</h2>
        {history.length === 0 ? (
          <p data-testid="ac-history-empty" className="liquid-glass-card py-6 text-center text-[13px] text-[#8E8E93]">Belum ada permintaan koreksi.</p>
        ) : (
          <div className="space-y-2">
            {history.map((h) => (
              <div key={h.id} data-testid={`ac-history-${h.id}`} className="flex items-center justify-between gap-3 rounded-2xl border border-black/[0.05] bg-white/70 p-3.5">
                <div className="min-w-0">
                  <p className="text-[13.5px] font-medium text-[#1D1D1F]">{h.employee_name} · {h.attendance_date}</p>
                  <p className="text-[11.5px] text-[#8E8E93]">{nice(h.current_result)} → {h.action === "ignore" ? "Ignore" : `${h.proposed_day_value} Day`} · “{h.reason}”</p>
                  {h.status === "rejected" && h.admin_note && <p className="mt-0.5 text-[11px] text-[#C0362C]">Catatan admin: {h.admin_note}</p>}
                </div>
                <div className="shrink-0 text-right">
                  <span className={`rounded-full px-2.5 py-1 text-[11.5px] font-medium ${STATUS_CHIP[h.status] || "bg-[#F1F1F4] text-[#6E6E73]"}`}>{STATUS_ID[h.status] || h.status}</span>
                  {h.status !== "pending" && h.reviewed_by && <p className="mt-1 flex items-center justify-end gap-1 text-[10.5px] text-[#8E8E93]">oleh {h.reviewed_by} <VerifiedBadge size={11} /></p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!propose} onOpenChange={(v) => !v && setPropose(null)}>
        <DialogContent data-testid="ac-propose-dialog" className="gap-0 rounded-[20px] border border-white/50 bg-white/90 p-6 backdrop-blur-2xl sm:max-w-[420px]">
          <DialogHeader className="text-left"><DialogTitle className="text-[17px] font-semibold">Ajukan Koreksi Absensi</DialogTitle></DialogHeader>
          {propose && (
            <div className="mt-3 space-y-3">
              <div className="rounded-xl bg-[#FAFAFA] px-3.5 py-2.5">
                <p className="text-[13.5px] font-medium">{propose.row.employee_name}</p>
                <p className="num text-[11.5px] text-[#8E8E93]">{propose.row.employee_nip} · {date} · {nice(propose.row.result)}</p>
              </div>
              <div>
                <label className="label mb-1 block">Usulan Hasil</label>
                <Select value={propose.action === "ignore" ? "ignore" : String(propose.day_value)} onValueChange={(v) => setPropose((p) => ({ ...p, action: v === "ignore" ? "ignore" : "approve", day_value: v === "ignore" ? 0 : Number(v) }))}>
                  <SelectTrigger data-testid="ac-propose-value" className="h-11 rounded-xl border-[#E1E1E6] bg-white/70 text-[14px]"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-xl">
                    <SelectItem value="1">1.0 Day</SelectItem>
                    <SelectItem value="0.5">0.5 Day</SelectItem>
                    <SelectItem value="0">0 Day</SelectItem>
                    <SelectItem value="ignore">Ignore</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="label mb-1 block">Alasan</label>
                <textarea data-testid="ac-propose-reason" value={propose.reason} onChange={(e) => setPropose((p) => ({ ...p, reason: e.target.value }))} rows={3} placeholder="Contoh: Izin pulang setengah hari." className="w-full rounded-xl border border-[#E1E1E6] bg-white/70 px-3 py-2.5 text-[14px]" />
              </div>
              {!readOnly && (
              <button data-testid="ac-propose-submit" onClick={submit} disabled={busy} className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-[#0071E3] text-[15px] font-medium text-white disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Kirim Permintaan
              </button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
